import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { CircleNotch } from '@phosphor-icons/react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  [
    'inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap',
    'rounded-md text-base font-medium',
    'transition-colors duration-150',
    'disabled:pointer-events-none disabled:opacity-50',
    '[&_svg]:pointer-events-none [&_svg]:shrink-0',
  ].join(' '),
  {
    variants: {
      variant: {
        // O primário JÁ é o botão com foco de luz. Era uma variante à parte
        // (`spotlight`) e virou o padrão: a vitrine tem seis CTAs primários
        // espalhados, e nenhum deles ia lembrar de pedir a variante. O efeito
        // em si mora no `.btn-spotlight`, em `clearance-theme.css`.
        default:
          'btn-spotlight bg-primary text-primary-foreground hover:bg-primary/90 active:bg-primary/95',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        outline:
          'border border-input bg-background text-foreground hover:bg-accent hover:text-accent-foreground',
        ghost: 'text-foreground hover:bg-accent hover:text-accent-foreground',
        destructive:
          'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        link: 'text-primary underline-offset-4 hover:underline',
        // Par foreground/background invertido: grafite no tema claro, creme no
        // escuro — destaca sobre qualquer fundo sem cor própria por tema. Hover
        // OPACO (mistura de tokens, não `/90`): em elemento flutuante, a
        // transparência deixaria a foto de trás aparecer pelo botão.
        inverse:
          'bg-foreground text-background hover:bg-[color-mix(in_oklab,var(--foreground)_88%,var(--background))] hover:text-background',
        new_outline:
          'rounded-full border-2 border-primary bg-background p-6 font-bold text-primary hover:bg-primary-soft hover:text-primary-soft-foreground',
      },
      size: {
        default: 'h-11 px-5 py-2.5 [&_svg]:size-5',
        sm: 'h-9 px-3 text-sm rounded-md [&_svg]:size-4',
        lg: 'h-12 px-6 text-lg rounded-lg [&_svg]:size-5',
        icon: 'h-11 w-11 [&_svg]:size-5',
        'icon-sm': 'h-9 w-9 rounded-md [&_svg]:size-4',
        // A ação principal de uma tela: 48px, totalmente arredondado, no
        // formato que a vitrine já usava à mão (`CartSummary`,
        // `AddToCartControl`) e que o login consolidou. A largura fica a cargo
        // de quem usa — `w-full` num formulário, automática numa barra.
        cta: 'h-12 rounded-full px-7 text-base font-semibold [&_svg]:size-5',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  /**
   * Ação em curso: troca o rótulo por um giro, desabilita e anuncia `aria-busy`.
   *
   * O rótulo **não some para quem usa leitor de tela** — ele continua como
   * `sr-only`, senão o botão perde o nome acessível justamente no momento em
   * que está trabalhando, e quem não vê a tela deixa de saber o que está em
   * curso.
   *
   * Ignorado com `asChild`: o `Slot` do Radix aceita um único filho, e trocar o
   * conteúdo por ícone + rótulo o quebraria. Botão que é link não costuma ter
   * estado de carregamento mesmo.
   */
  loading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      loading = false,
      disabled,
      children,
      onMouseMove,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : 'button';
    const carregando = loading && !asChild;

    /**
     * A posição do ponteiro em porcentagem, para o gradiente do
     * `.btn-spotlight` se ancorar. Escreve direto no `style` do elemento em vez
     * de passar por estado: é um valor por quadro de `mousemove`, e um
     * `setState` aí re-renderizaria a árvore inteira do botão a cada pixel.
     *
     * Só é instalado no primário — `outline`, `ghost`, `link` e companhia não
     * têm o brilho e não pagam listener nenhum.
     */
    const trackPointer = (event: React.MouseEvent<HTMLElement>) => {
      const alvo = event.currentTarget;
      const rect = alvo.getBoundingClientRect();
      alvo.style.setProperty(
        '--x',
        `${((event.clientX - rect.left) / rect.width) * 100}%`
      );
      alvo.style.setProperty(
        '--y',
        `${((event.clientY - rect.top) / rect.height) * 100}%`
      );
      onMouseMove?.(event as React.MouseEvent<HTMLButtonElement>);
    };

    return (
      <Comp
        ref={ref}
        data-size={size ?? 'default'}
        disabled={carregando || disabled}
        aria-busy={carregando || undefined}
        onMouseMove={
          variant === undefined || variant === 'default'
            ? trackPointer
            : onMouseMove
        }
        className={cn(buttonVariants({ variant, size, className }))}
        {...props}
      >
        {carregando ? (
          <>
            <CircleNotch className="animate-spin" aria-hidden />
            <span className="sr-only">{children}</span>
          </>
        ) : (
          children
        )}
      </Comp>
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
